import React, { useState, useEffect, lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from '../context/AuthContext';
import { trackPageView } from './Analytics';
import { showWarning } from '../utils/toast';
import { DarkModeProvider } from '../context/DarkModeContext';

// Keep header/footer synchronous (always visible on marketing pages)
import MarketingHeader from './MarketingHeader';
import MarketingFooter from './MarketingFooter';

// Lazy load all heavy components for better performance
const LandingPage = lazy(() => import('./LandingPage'));
const PricingPage = lazy(() => import('./PricingPage'));
const MainApp = lazy(() => import('./MainApp'));
const CalendarPage = lazy(() => import('./CalendarPage'));
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const AdminSettings = lazy(() => import('./AdminSettings'));
const AdminBlogManager = lazy(() => import('./AdminBlogManager'));
const SystemMonitoring = lazy(() => import('./SystemMonitoring'));
const AdminEmailTemplates = lazy(() => import('./AdminEmailTemplates'));
const Reports = lazy(() => import('./Reports'));
const Blog = lazy(() => import('./Blog'));
const ConversationsPage = lazy(() => import('./ConversationsPage'));
const AuthModal = lazy(() => import('./AuthModal'));
const PricingModal = lazy(() => import('./PricingModal'));
const PaymentSuccess = lazy(() => import('./PaymentSuccess'));
const PasswordResetPage = lazy(() => import('./PasswordResetPage'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const TermsOfService = lazy(() => import('./TermsOfService'));
const CookiePolicy = lazy(() => import('./CookiePolicy'));
const GDPRCompliance = lazy(() => import('./GDPRCompliance'));
const AboutUs = lazy(() => import('./AboutUs'));
const HelpCenter = lazy(() => import('./HelpCenter'));
const Dashboard = lazy(() => import('./Dashboard'));
const NotFound = lazy(() => import('./NotFound'));
const SubscriptionSuccess = lazy(() => import('./SubscriptionSuccess'));
const SubscriptionCancel = lazy(() => import('./SubscriptionCancel'));

// New dashboard components - lazy loaded
const UserDashboard = lazy(() => import('./user-dashboard/UserDashboard'));
const AdminDashboardNew = lazy(() => import('./admin-dashboard/AdminDashboardNew'));
const OnboardingWizard = lazy(() => import('./OnboardingWizard'));

// Loading spinner component for Suspense fallback
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-100 dark:bg-neutral-900 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

const AppRouter = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [paymentSuccessOpen, setPaymentSuccessOpen] = useState(false);
  const { isAuthenticated, user, loading, needsOnboarding, completeOnboarding } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Show onboarding wizard for new users
  useEffect(() => {
    if (isAuthenticated && needsOnboarding && !loading) {
      setShowOnboarding(true);
    }
  }, [isAuthenticated, needsOnboarding, loading]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    completeOnboarding();
  };

  // Check URL and set initial page
  useEffect(() => {
    const checkRoute = () => {
      const path = window.location.pathname;
      const hash = window.location.hash;

      // Don't make routing decisions while authentication is loading
      if (loading) {
        return;
      }

      // Handle OAuth callback - redirect to app after authentication
      if (path === '/auth/callback') {
        // Supabase handles the token parsing automatically with detectSessionInUrl: true
        // Just redirect to app page after auth completes
        if (isAuthenticated) {
          setCurrentPage('app');
          window.history.replaceState({}, '', '/app');
        }
        return; // Don't process other routes during callback
      }

      // Handle direct page routes first
      if (path === '/dashboard') {
        if (isAuthenticated) {
          setCurrentPage('dashboard');
        } else {
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/pricing') {
        setCurrentPage('pricing');
      } else if (path === '/blog' || path.startsWith('/blog/')) {
        setCurrentPage('blog');
      } else if (path === '/privacy') {
        setCurrentPage('privacy');
      } else if (path === '/terms') {
        setCurrentPage('terms');
      } else if (path === '/cookie-policy') {
        setCurrentPage('cookie-policy');
      } else if (path === '/gdpr') {
        setCurrentPage('gdpr');
      } else if (path === '/about') {
        setCurrentPage('about');
      } else if (path === '/help') {
        setCurrentPage('help');
      } else if (path === '/app' || path.startsWith('/app/')) {
        if (isAuthenticated) {
          setCurrentPage('app');
        } else {
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/admin') {
        if (isAuthenticated && user?.is_admin) {
          setCurrentPage('admin');
        } else if (isAuthenticated) {
          showWarning('Admin access required');
          setCurrentPage('app');
        } else {
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/admin-settings') {
        if (isAuthenticated && user?.is_admin) {
          setCurrentPage('admin-settings');
        } else if (isAuthenticated) {
          showWarning('Admin access required');
          setCurrentPage('app');
        } else {
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/admin-blog') {
        if (isAuthenticated && user?.is_admin) {
          setCurrentPage('admin-blog');
        } else if (isAuthenticated) {
          showWarning('Admin access required');
          setCurrentPage('app');
        } else {
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/admin/email-templates') {
        if (isAuthenticated && user?.is_admin) {
          setCurrentPage('admin-email-templates');
        } else if (isAuthenticated) {
          showWarning('Admin access required');
          setCurrentPage('app');
        } else {
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/system-monitoring') {
        if (isAuthenticated && user?.is_admin) {
          setCurrentPage('system-monitoring');
        } else if (isAuthenticated) {
          showWarning('Admin access required');
          setCurrentPage('app');
        } else {
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/calendar') {
        if (isAuthenticated) {
          setCurrentPage('calendar');
        } else {
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/reports') {
        if (isAuthenticated) {
          setCurrentPage('reports');
        } else {
          setCurrentPage('reports'); // Set the page even if not authenticated
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/conversations') {
        if (isAuthenticated) {
          setCurrentPage('conversations');
        } else {
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/reset-password') {
        setCurrentPage('reset-password');
      } else if (path === '/subscription-success') {
        // Subscription success page - requires authentication to verify payment
        if (isAuthenticated) {
          setCurrentPage('subscription-success');
        } else {
          // Store the URL to redirect back after login
          setAuthModalOpen(true);
          setAuthModalMode('login');
        }
      } else if (path === '/subscription-cancel') {
        // Subscription cancelled page - no auth required
        setCurrentPage('subscription-cancel');
      } else if (path === '/' || path === '') {
        // Handle home page route - preserve authentication state during navigation
        // Don't re-evaluate authentication if user is already authenticated and intentionally visiting home
        if (isAuthenticated && user && currentPage !== 'home') {
          // User is authenticated and navigating to home page (e.g., by clicking logo)
          // Just set the page without triggering authentication re-evaluation
          setCurrentPage('home');
        } else if (!isAuthenticated) {
          // Handle hash-based navigation on home page for non-authenticated users
          if (hash === '#features') {
            setCurrentPage('features');
            setTimeout(() => scrollToSection('features'), 200);
          } else if (hash === '#about') {
            setCurrentPage('about');
            setTimeout(() => scrollToSection('about'), 200);
          } else if (hash === '#pricing') {
            setCurrentPage('pricing');
            // Redirect to proper pricing page
            window.history.replaceState({}, '', '/pricing');
          } else {
            setCurrentPage('home');
          }
        } else if (currentPage !== 'home') {
          // Default case - set to home page
          setCurrentPage('home');
        }
      } else {
        // Unknown route - show 404 page
        setCurrentPage('404');
      }
    };

    const scrollToSection = (sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };

    checkRoute();

    // Listen for back/forward navigation
    window.addEventListener('popstate', checkRoute);
    return () => window.removeEventListener('popstate', checkRoute);
  }, [isAuthenticated, user, loading]);

  // Auto-redirect users to appropriate dashboard when they login (but allow them to visit home page if they choose)
  useEffect(() => {

    if (isAuthenticated && user) {
      // Only redirect admin users to admin dashboard if they're trying to access the regular app dashboard
      // Don't redirect them if they're on home page (they may have clicked logo intentionally)
      if (user.is_admin && currentPage === 'app') {
        setCurrentPage('admin');
        window.history.pushState({}, '', '/admin');
      }
      // Don't automatically redirect regular users away from home page
      // Let them visit home page if they choose to (e.g., by clicking logo)
    }
  }, [isAuthenticated, user, currentPage]);

  // Track page views for Google Analytics
  useEffect(() => {
    if (!loading && currentPage) {
      const pageTitles = {
        'home': 'SubscriptionManager - Subscription Management Dashboard',
        'pricing': 'Pricing - SubscriptionManager',
        'blog': 'Blog - SubscriptionManager',
        'privacy': 'Privacy Policy - SubscriptionManager',
        'terms': 'Terms of Service - SubscriptionManager',
        'cookie-policy': 'Cookie Policy - SubscriptionManager',
        'gdpr': 'GDPR Compliance - SubscriptionManager',
        'about': 'About Us - SubscriptionManager',
        'help': 'Help Center - SubscriptionManager',
        'app': 'Dashboard - SubscriptionManager',
        'dashboard': 'New Dashboard - Zarss',
        'calendar': 'Calendar - SubscriptionManager',
        'conversations': 'Conversations - SubscriptionManager',
        'reports': 'Reports - SubscriptionManager',
        'admin': 'Admin Dashboard - SubscriptionManager',
        'admin-settings': 'Admin Settings - SubscriptionManager',
        'admin-email-templates': 'Email Templates - SubscriptionManager',
        'admin-blog': 'Blog Management - SubscriptionManager',
        'system-monitoring': 'System Monitoring - SubscriptionManager',
        'subscription-success': 'Subscription Success - SubscriptionManager',
        'subscription-cancel': 'Subscription Cancelled - SubscriptionManager',
        '404': 'Page Not Found - SubscriptionManager',
      };

      const pageTitle = pageTitles[currentPage] || 'SubscriptionManager';
      const pagePath = window.location.pathname;

      // Track the page view
      trackPageView(pagePath, pageTitle);

      // Update document title
      document.title = pageTitle;
    }
  }, [currentPage, loading]);

  // Force light mode for marketing pages (homepage should always be light)
  useEffect(() => {
    const marketingPages = ['home', 'pricing', 'blog', 'privacy', 'terms', 'cookie-policy', 'gdpr', 'about', 'contact', 'help'];
    const isMarketingSite = !isAuthenticated || marketingPages.includes(currentPage);

    if (isMarketingSite) {
      document.documentElement.classList.remove('dark');
    }
  }, [currentPage, isAuthenticated]);

  // Check for payment success in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');

    if (sessionId) {
      setPaymentSuccessOpen(true);
      setCurrentPage('app');
      // Don't clean up URL here - let PaymentSuccess component handle it after processing
    } else if (paymentStatus === 'success') {
      // Handle PayPal return without session_id
      setCurrentPage('app');
      // Clean up the payment parameter
      urlParams.delete('payment');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    } else if (paymentStatus === 'cancelled') {
      // Handle PayPal cancellation
      setCurrentPage('app');
      // Clean up the payment parameter
      urlParams.delete('payment');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }

    // Check for login parameter to auto-open auth modal
    if (urlParams.get('login') === 'true') {
      setAuthModalOpen(true);
      setAuthModalMode('login');
      // Clean up URL parameter
      urlParams.delete('login');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleNavigation = (page, hash = null) => {
    // Handle authentication requirements
    if ((page === 'app' || page === 'calendar' || page === 'conversations') && !isAuthenticated) {
      setAuthModalOpen(true);
      setAuthModalMode('login');
      return;
    }

    if (page === 'dashboard' && !isAuthenticated) {
      setAuthModalOpen(true);
      setAuthModalMode('login');
      return;
    }

    if ((page === 'admin' || page === 'admin-settings' || page === 'admin-email-templates' || page === 'admin-blog' || page === 'system-monitoring') && (!isAuthenticated || !user?.is_admin)) {
      if (!isAuthenticated) {
        setAuthModalOpen(true);
        setAuthModalMode('login');
      } else {
        showWarning('Admin access required');
      }
      return;
    }

    // Set the current page state
    setCurrentPage(page);

    // Handle URL updates and scrolling
    switch (page) {
      case 'home':
        const homeUrl = hash ? `/#${hash}` : '/';
        window.history.pushState({}, '', homeUrl);

        if (hash) {
          // Navigate to home with specific section
          setTimeout(() => scrollToSection(hash), 100);
        } else {
          // Navigate to home top
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        break;

      case 'pricing':
        window.history.pushState({}, '', '/pricing');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'features':
        window.history.pushState({}, '', '/#features');
        setTimeout(() => scrollToSection('features'), 200);
        break;



      case 'blog':
        window.history.pushState({}, '', '/blog');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'privacy':
        window.history.pushState({}, '', '/privacy');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'terms':
        window.history.pushState({}, '', '/terms');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'cookie-policy':
        window.history.pushState({}, '', '/cookie-policy');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'gdpr':
        window.history.pushState({}, '', '/gdpr');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'about':
        window.history.pushState({}, '', '/about');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'help':
        window.history.pushState({}, '', '/help');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'subscription-success':
        window.history.pushState({}, '', '/subscription-success');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'subscription-cancel':
        window.history.pushState({}, '', '/subscription-cancel');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'app':
        window.history.pushState({}, '', '/app');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'dashboard':
        window.history.pushState({}, '', '/dashboard');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'settings':
        // Redirect to app with settings view
        setCurrentPage('app');
        window.history.pushState({}, '', '/app/settings');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'subscription':
        // Redirect to app with billing/subscription view  
        setCurrentPage('app');
        window.history.pushState({}, '', '/app/subscription');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'calendar':
        window.history.pushState({}, '', '/calendar');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'reports':
        window.history.pushState({}, '', '/reports');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'conversations':
        window.history.pushState({}, '', '/conversations');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'admin':
        window.history.pushState({}, '', '/admin');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'admin-settings':
        window.history.pushState({}, '', '/admin-settings');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'admin-email-templates':
        window.history.pushState({}, '', '/admin/email-templates');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'admin-blog':
        window.history.pushState({}, '', '/admin-blog');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'system-monitoring':
        window.history.pushState({}, '', '/system-monitoring');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'logout':
        // Handle logout - this will be caught by AuthContext
        // For now, just navigate to home - actual logout is handled by useAuth().logout()
        window.history.pushState({}, '', '/');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      default:
        break;
    }
  };

  // Helper function for scrolling to sections
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      setCurrentPage('app');
      window.history.pushState({}, '', '/app');
    } else {
      setAuthModalOpen(true);
      setAuthModalMode('register');
    }
  };

  const handleLogin = () => {
    setAuthModalOpen(true);
    setAuthModalMode('login');
  };

  const handleViewPricing = () => {
    setCurrentPage('pricing');
    window.history.pushState({}, '', '/pricing');
    // Always scroll to top when navigating to pricing page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleUpgrade = (planId) => {
    setPricingModalOpen(true);
    // Set the selected plan for the modal
    if (planId) {
      // Pass planId to PricingModal - we'll need to update the modal to handle initial selection
    }
  };

  const handleAuthSuccess = () => {
    setAuthModalOpen(false);

    // Check the current URL path to determine where to redirect
    const currentPath = window.location.pathname;

    if (currentPath === '/admin' && user?.is_admin) {
      setCurrentPage('admin');
      window.history.pushState({}, '', '/admin');
    } else if (currentPath === '/calendar') {
      setCurrentPage('calendar');
      window.history.pushState({}, '', '/calendar');
    } else if (currentPath === '/conversations') {
      setCurrentPage('conversations');
      window.history.pushState({}, '', '/conversations');
    } else if (currentPath === '/app') {
      setCurrentPage('app');
      window.history.pushState({}, '', '/app');
    } else if (user?.is_admin) {
      // Default admin users to admin dashboard
      setCurrentPage('admin');
      window.history.pushState({}, '', '/admin');
    } else {
      // Default regular users to main app
      setCurrentPage('app');
      window.history.pushState({}, '', '/app');
    }
  };

  // Render marketing pages (all lazy loaded with Suspense)
  const renderMarketingPage = () => {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        {(() => {
          switch (currentPage) {
            case 'pricing':
              return (
                <PricingPage
                  onGetStarted={handleGetStarted}
                  onUpgrade={handleUpgrade}
                />
              );
            case 'blog':
              return <Blog onNavigate={handleNavigation} />;
            case 'privacy':
              return <PrivacyPolicy onBack={() => handleNavigation('home')} />;
            case 'terms':
              return <TermsOfService onBack={() => handleNavigation('home')} />;
            case 'cookie-policy':
              return <CookiePolicy onBack={() => handleNavigation('home')} />;
            case 'gdpr':
              return <GDPRCompliance onBack={() => handleNavigation('home')} />;
            case 'about':
              return <AboutUs onBack={() => handleNavigation('home')} onGetStarted={handleGetStarted} />;
            case 'help':
              return <HelpCenter onBack={() => handleNavigation('home')} />;
            case '404':
              return <NotFound onNavigate={handleNavigation} />;
            case 'home':
            case 'features':
            default:
              return (
                <LandingPage
                  key={currentPage}
                  onGetStarted={handleGetStarted}
                  onLogin={handleLogin}
                  onViewPricing={handleViewPricing}
                />
              );
          }
        })()}
      </Suspense>
    );
  };

  // Show loading screen while authentication is being determined
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show onboarding wizard if needed (renders on top of any page)
  const renderOnboardingWizard = () => {
    if (showOnboarding && isAuthenticated) {
      return (
        <Suspense fallback={null}>
          <OnboardingWizard
            isOpen={showOnboarding}
            onComplete={handleOnboardingComplete}
            userEmail={user?.email}
            userId={user?.id}
          />
        </Suspense>
      );
    }
    return null;
  };

  // If user is on new dashboard page and authenticated
  if (currentPage === 'dashboard' && isAuthenticated) {
    return (
      <DarkModeProvider>
        {renderOnboardingWizard()}
        <Suspense fallback={<LoadingSpinner />}>
          <Dashboard />
        </Suspense>
      </DarkModeProvider>
    );
  }

  // If user is on app page and authenticated, show new user dashboard
  if (currentPage === 'app' && isAuthenticated) {
    return (
      <>
        {renderOnboardingWizard()}
        <Suspense fallback={<LoadingSpinner />}>
          <UserDashboard onNavigate={handleNavigation} />
        </Suspense>
      </>
    );
  }

  // If user is on calendar page and authenticated, show calendar page with dark mode support
  if (currentPage === 'calendar' && isAuthenticated) {
    return (
      <DarkModeProvider>
        {renderOnboardingWizard()}
        <Suspense fallback={<LoadingSpinner />}>
          <CalendarPage onNavigate={handleNavigation} />
        </Suspense>
      </DarkModeProvider>
    );
  }

  // If user is on reports page, show reports page (with auth check and dark mode support)
  if (currentPage === 'reports') {
    if (isAuthenticated) {
      return (
        <DarkModeProvider>
          {renderOnboardingWizard()}
          <Suspense fallback={<LoadingSpinner />}>
            <Reports onBack={() => handleNavigation('app')} />
          </Suspense>
        </DarkModeProvider>
      );
    } else {
      // Show reports page with login prompt for unauthenticated users
      return (
        <AuthProvider>
          <div className="min-h-screen bg-gray-100">
            <MarketingHeader onNavigate={handleNavigation} />
            <Suspense fallback={<LoadingSpinner />}>
              <Reports onBack={() => handleNavigation('home')} />
            </Suspense>
            <MarketingFooter onNavigate={handleNavigation} />
            <Suspense fallback={null}>
              <AuthModal
                isOpen={authModalOpen}
                onClose={() => setAuthModalOpen(false)}
                initialMode={authModalMode}
                onToggleMode={(mode) => setAuthModalMode(mode)}
              />
            </Suspense>
          </div>
        </AuthProvider>
      );
    }
  }

  // If user is on conversations page and authenticated, show conversations page with dark mode support
  if (currentPage === 'conversations' && isAuthenticated) {
    return (
      <DarkModeProvider>
        {renderOnboardingWizard()}
        <Suspense fallback={<LoadingSpinner />}>
          <ConversationsPage user={user} onNavigate={handleNavigation} />
        </Suspense>
      </DarkModeProvider>
    );
  }

  // If user is on admin page and is authenticated admin, show new admin dashboard
  if (currentPage === 'admin' && isAuthenticated && user?.is_admin) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <AdminDashboardNew
          onNavigate={handleNavigation}
          onLogout={() => handleNavigation('logout')}
        />
      </Suspense>
    );
  }

  // If user is on admin settings page and is authenticated admin, show admin settings with dark mode support
  if (currentPage === 'admin-settings' && isAuthenticated && user?.is_admin) {
    return (
      <DarkModeProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <AdminSettings onBack={() => handleNavigation('admin')} />
        </Suspense>
      </DarkModeProvider>
    );
  }

  // If user is on admin blog page and is authenticated admin, show admin blog manager with dark mode support
  if (currentPage === 'admin-blog' && isAuthenticated && user?.is_admin) {
    return (
      <DarkModeProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <AdminBlogManager onBack={() => handleNavigation('admin')} />
        </Suspense>
      </DarkModeProvider>
    );
  }

  // If user is on system monitoring page and is authenticated admin, show system monitoring with dark mode support
  if (currentPage === 'system-monitoring' && isAuthenticated && user?.is_admin) {
    return (
      <DarkModeProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <SystemMonitoring onBack={() => handleNavigation('admin')} />
        </Suspense>
      </DarkModeProvider>
    );
  }

  // If user is on admin email templates page and is authenticated admin, show email templates with dark mode support
  if (currentPage === 'admin-email-templates' && isAuthenticated && user?.is_admin) {
    return (
      <DarkModeProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <AdminEmailTemplates onBack={() => handleNavigation('admin')} />
        </Suspense>
      </DarkModeProvider>
    );
  }

  // If user is on password reset page, show password reset
  if (currentPage === 'reset-password') {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <PasswordResetPage />
      </Suspense>
    );
  }

  // If user is on subscription success page and authenticated
  if (currentPage === 'subscription-success' && isAuthenticated) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <SubscriptionSuccess onNavigate={handleNavigation} />
      </Suspense>
    );
  }

  // If user is on subscription cancel page
  if (currentPage === 'subscription-cancel') {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <SubscriptionCancel onNavigate={handleNavigation} />
      </Suspense>
    );
  }


  // Show marketing site
  return (
    <div className="min-h-screen bg-white">
      <MarketingHeader
        onLogin={handleLogin}
        onGetStarted={handleGetStarted}
        currentPage={currentPage}
        onNavigate={handleNavigation}
      />

      <main>
        {renderMarketingPage()}
      </main>

      <MarketingFooter onNavigate={handleNavigation} />

      {/* Modals - lazy loaded */}
      <Suspense fallback={null}>
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          initialMode={authModalMode}
          onSuccess={handleAuthSuccess}
        />
      </Suspense>

      <Suspense fallback={null}>
        <PricingModal
          isOpen={pricingModalOpen}
          onClose={() => setPricingModalOpen(false)}
        />
      </Suspense>

      {paymentSuccessOpen && (
        <Suspense fallback={null}>
          <PaymentSuccess
            onClose={() => setPaymentSuccessOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
};

// Wrapper with AuthProvider
const App = () => {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
};

export default App;